import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Download, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import InvitationCard from "./InvitationCard";

export default function Footer() {
  const { t } = useLang();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const [{ default: html2canvas }, jsPdfModule] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const jsPDF = jsPdfModule.jsPDF || jsPdfModule.default;
      if (!cardRef.current) throw new Error("card not ready");

      // Wait one frame so fonts/layout are stable
      await new Promise((r) => requestAnimationFrame(r));

      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: "#FBF9F6",
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.95);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, "JPEG", 0, 0, pageW, pageH, undefined, "FAST");
      pdf.save("Invito-Natanael-Raluka-09.08.2026.pdf");
    } catch (err) {
      console.error("PDF download failed", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <footer className="relative py-16 md:py-20 px-6 md:px-12 bg-ink text-ivory overflow-hidden">
      <InvitationCard ref={cardRef} />

      <div
        className="brush-blob bg-burgundy"
        style={{
          width: 420,
          height: 420,
          bottom: "-200px",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      />
      <div className="max-w-5xl mx-auto text-center relative">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="font-script text-5xl md:text-7xl text-ivory mb-4"
          data-testid="footer-title"
        >
          Natanael & Raluka
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="text-xs uppercase tracking-[0.4em] text-ivory/70 mb-10"
        >
          {t.footer.date}
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.3 }}
          onClick={handleDownload}
          disabled={downloading}
          className="inline-flex items-center gap-3 px-8 py-3.5 border border-ivory/30 text-ivory rounded-full text-xs uppercase tracking-[0.35em] hover:bg-ivory hover:text-ink hover:border-ivory transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="footer-download-pdf-button"
        >
          {downloading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              {t.footer.downloading}
            </>
          ) : (
            <>
              <Download size={14} />
              {t.footer.download}
            </>
          )}
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
          className="flex items-center justify-center gap-2 text-ivory/50 text-xs uppercase tracking-[0.3em] mt-12"
        >
          {t.footer.made}
          <Heart size={12} fill="currentColor" className="text-burgundy" />
          2026
        </motion.div>
      </div>
    </footer>
  );
}
